import { Dashboard, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAppLocaleContext } from './locale_provider';

type DashboardContextType = {
  items: Dashboard[];
  fetchData: () => Promise<ServiceResponse<Dashboard[]>>;
  isLoading: boolean;
  error: ErrorResponse | null;
};

const DashboardContext = createContext({
  items: [],
  fetchData: async () => ({ type: 'client-error', message: 'Not implemented' }),
  isLoading: false,
  error: null,
} as DashboardContextType);

export function useDashboardContext() {
  return useContext(DashboardContext);
}

export function DashboardProvider({
  children,
  config,
  mode,
}: {
  children: React.ReactNode;
  config: string;
  mode: 'main' | 'sub';
}) {
  const [items, setItems] = useState<Dashboard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();
  const { localeId } = useAppLocaleContext();

  const fetchData = async (): Promise<ServiceResponse<Dashboard[]>> => {
    setIsLoading(true);
    let response: ServiceResponse<Dashboard[]>;

    if (mode === 'main') {
      response = await bzbsService.dashboardApi.mainDashboard({ appName: config, locale: localeId });
    } else {
      response = await bzbsService.dashboardApi.subDashboard({ dashboardName: config, locale: localeId });
    }
    setIsLoading(false);

    if (response.type === 'success') {
      setItems(response.model);
    } else {
      setError(response);
    }

    return response;
  };

  return (
    <DashboardContext.Provider
      value={{
        items,
        fetchData,
        isLoading,
        error,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}
